import PropTypes from "prop-types";
import ChevronDownIcon from "@untitled-ui/icons-react/build/esm/ChevronDown";
import ChevronRightIcon from "@untitled-ui/icons-react/build/esm/ChevronRight";
import {
  Box,
  ButtonBase,
  ListItemButton,
  ListItemText,
  SvgIcon,
} from "@mui/material";
import {
  Dropdown,
  DropdownMenu,
  DropdownTrigger,
} from "src/components/dropdown";
import { RouterLink } from "src/components/router-link";

const renderChildItems = ({ items, depth = 0 }) => {
  var fontSize = 11;
  var px = 1;
  var py = 0.3;
  return items.map((item) => {
    // Branch
    if (item.items) {
      const branchLinkProps = item.path
        ? item.external
          ? {
              component: "a",
              href: item.path,
              target: "_blank",
            }
          : {
              component: RouterLink,
              href: item.path,
            }
        : {};

      return (
        <Dropdown key={item.title}>
          <Box sx={{ display: "flex", alignItems: "stretch" }}>
            <ListItemButton
              disabled={item.disabled}
              sx={{
                borderRadius: 1,
                flexGrow: 1,
                px,
                py,
              }}
              {...branchLinkProps}
            >
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  sx: {
                    color: "text.secondary",
                    fontSize,
                    fontWeight: 500,
                  },
                }}
              />
            </ListItemButton>
            <DropdownTrigger>
              <ListItemButton
                aria-label={`${item.title} 하위 메뉴`}
                disabled={item.disabled}
                sx={{ borderRadius: 1, flexGrow: 0, px: 0.5, py }}
              >
                <SvgIcon
                  fontSize="small"
                  sx={{ color: "neutral.400" }}
                >
                  <ChevronRightIcon />
                </SvgIcon>
              </ListItemButton>
            </DropdownTrigger>
          </Box>
          <DropdownMenu
            anchorOrigin={{
              horizontal: "right",
              vertical: "top",
            }}
            disableScrollLock
            PaperProps={{
              elevation: 8,
              sx: {
                maxWidth: "100%",
                ml: 1,
                p: 1,
                width: 150,
              },
            }}
            transformOrigin={{
              horizontal: "left",
              vertical: "top",
            }}
          >
            {renderChildItems({ items: item.items, depth: depth + 1 })}
          </DropdownMenu>
        </Dropdown>
      );
    }

    const linkProps = item.path
      ? item.external
        ? {
            component: "a",
            href: item.path,
            target: "_blank",
          }
        : {
            component: RouterLink,
            href: item.path,
          }
      : {};

    // Leaf
    return (
      <ListItemButton
        disabled={item.disabled}
        key={item.title}
        sx={{
          borderRadius: 1,
          px,
          py,
        }}
        {...linkProps}
      >
        <ListItemText
          primary={item.title}
          primaryTypographyProps={{
            sx: {
              color: "text.secondary",
              fontSize,
              fontWeight: 500,
            },
          }}
        />
      </ListItemButton>
    );
  });
};

export const TopNavItem = (props) => {
  const { active, disabled, external, items, icon, label, path, title } = props;

  // With dropdown
  const px = "3px";
  const py = "0px";
  const mr = 0.5;
  const width = "80px";

  if (items) {
    const branchLinkProps = path
      ? external
        ? {
            component: "a",
            href: path,
            target: "_blank",
          }
        : {
            component: RouterLink,
            href: path,
          }
      : {};

    return (
      <Dropdown>
        <DropdownTrigger>
          <Box
            sx={{
              alignItems: "stretch",
              borderRadius: 1,
              display: "flex",
              width,
              pointerEvents: disabled ? "none" : "auto",
              ...(active && {
                backgroundColor: "var(--nav-item-active-bg)",
              }),
              "&:hover": {
                backgroundColor: "var(--nav-item-hover-bg)",
              },
            }}
          >
            <ButtonBase
              disabled={disabled}
              sx={{
                alignItems: "center",
                display: "flex",
                flexGrow: 1,
                justifyContent: "flex-start",
                minWidth: 0,
                pl: px,
                py,
                textAlign: "left",
              }}
              {...branchLinkProps}
            >
              <Box
                component="span"
                sx={{
                  alignItems: "center",
                  color: "var(--nav-item-icon-color)",
                  display: "inline-flex",
                  justifyContent: "center",
                  mr,
                  ...(active && {
                    color: "var(--nav-item-icon-active-color)",
                  }),
                }}
              >
                {icon}
              </Box>
              <Box
                component="span"
                sx={{
                  color: "var(--nav-item-color)",
                  flexGrow: 1,
                  fontFamily: (theme) => theme.typography.fontFamily,
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: "20px",
                  whiteSpace: "nowrap",
                  ...(active && {
                    color: "var(--nav-item-active-color)",
                  }),
                  ...(disabled && {
                    color: "var(--nav-item-disabled-color)",
                  }),
                }}
              >
                {title}
              </Box>
            </ButtonBase>
            <ButtonBase
              aria-label={`${title} 하위 메뉴`}
              disabled={disabled}
              sx={{
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
                px: "2px",
                py,
              }}
            >
              <SvgIcon
                sx={{
                  color: "var(--nav-item-chevron-color)",
                  fontSize: 16,
                  // ml: 1,
                }}
              >
                <ChevronDownIcon />
              </SvgIcon>
            </ButtonBase>
          </Box>
        </DropdownTrigger>
        <DropdownMenu
          disableScrollLock
          PaperProps={{
            elevation: 8,
            sx: {
              maxWidth: "100%",
              // p: 1,
              width: 200,
            },
          }}
        >
          {renderChildItems({ items, depth: 0 })}
        </DropdownMenu>
      </Dropdown>
    );
  }

  // Without dropdown

  const linkProps = path
    ? external
      ? {
          component: "a",
          href: path,
          target: "_blank",
        }
      : {
          component: RouterLink,
          href: path,
        }
    : {};

  return (
    <ButtonBase
      disabled={disabled}
      sx={{
        alignItems: "center",
        borderRadius: 1,
        display: "flex",
        justifyContent: "flex-start",
        px,
        py,
        textAlign: "left",
        width,
        ...(active && {
          backgroundColor: "var(--nav-item-active-bg)",
        }),
        "&:hover": {
          backgroundColor: "var(--nav-item-hover-bg)",
        },
      }}
      {...linkProps}
    >
      {icon && (
        <Box
          component="span"
          sx={{
            alignItems: "center",
            color: "var(--nav-item-icon-color)",
            display: "inline-flex",
            justifyContent: "center",
            mr,
            ...(active && {
              color: "var(--nav-item-icon-active-color)",
            }),
          }}
        >
          {icon}
        </Box>
      )}
      <Box
        component="span"
        sx={{
          color: "var(--nav-item-color)",
          flexGrow: 1,
          fontFamily: (theme) => theme.typography.fontFamily,
          fontSize: 11,
          fontWeight: 600,
          lineHeight: "20px",
          whiteSpace: "nowrap",
          ...(active && {
            color: "var(--nav-item-active-color)",
          }),
          ...(disabled && {
            color: "var(--nav-item-disabled-color)",
          }),
        }}
      >
        {title}
      </Box>
      {label && (
        <Box
          component="span"
          sx={{ ml: 1 }}
        >
          {label}
        </Box>
      )}
    </ButtonBase>
  );
};

TopNavItem.propTypes = {
  active: PropTypes.bool,
  external: PropTypes.bool,
  icon: PropTypes.node,
  items: PropTypes.array,
  label: PropTypes.node,
  path: PropTypes.string,
  title: PropTypes.string.isRequired,
};
